import React, { useMemo } from 'react';
import { Field, FieldArray, Form, Formik } from 'formik';
import moment from 'moment';
import { keyBy, mapValues, pick, sortBy, uniq } from 'lodash';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { DefaultButton } from 'office-ui-fabric-react/lib/Button';
import {
  DetailsRow,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  SelectionMode,
} from 'office-ui-fabric-react';
import PropTypes from 'prop-types';
import StackGrid from '../../StackGrid';
import Dropdown from '../../Dropdown';
import MultiPickerFormik from '../../MultiPicker/MultiPickerFormik';
import DynamicDetailsList from '../../DynamicDetailsList/DynamicDetailsList';
import {
  getAllColumnsWithOperators,
  defaultPivotWiderTransformer,
  defaultTransformer,
} from '../../DynamicDetailsList/ContractTransactionColumns';
import { calcFeatureTypes, calculationMethods } from '../constants';
import styles from './CalculationFeatureForm.style';
import TextButton from '../../TextButton';
import { effectiveDateColumn, expirationDateColumn } from '../../DynamicDetailsList/DateColumns';
import TextField from '../../TextField';
import { pivotWider, rowTransformer } from '../../DynamicDetailsList/RowTransformation';
import { menuColumn } from '../../DynamicDetailsList/generalColumns';
import theme from '../../../theme.style';
import { pxToRem } from '../../../utils';

export const CalcFeatureTypeTestId = 'CalcFeatureTypeTestId';
export const CalcMethodTestId = 'CalcMethodTestId';
export const DimensionsPickerTestId = 'DimensionsPickerTestId';
export const AddAssignmentButtonTestId = 'AddAssignmentButtonTestId';
export const SaveAndContinueButtonTestId = 'SaveAndContinueButtonTestId';
export const CalculationRowTestId = 'CalculationRowTestId';

const defaultCalcFeature = () => ({
  featureType: '',
  calculationMethod: '',
  dimensions: [],
  calculations: [],
});

const createNewAssignment = contract => ({
  effectiveDate: contract.effectiveDate,
  terminationDate: contract.terminationDate,
});

const getDimensionsForCalculationFeatureType = featureType => {
  return calcFeatureTypes[featureType]?.dimensions || [];
};

const getCalcMethod = values => calculationMethods[values.calculationMethod];
const getCalcColumns = values => getCalcMethod(values).fields.map(field => field.key);

const getCalcParams = (values, calculation) => {
  const calculationMethodColumns = getCalcColumns(values);
  return pick(calculation, calculationMethodColumns);
};

const CalculationFeatureForm = ({ calculationFeature, contract, store, onSubmit, onCancel }) => {
  const calcFeatureOptions = [];
  const allColumns = useMemo(
    () =>
      getAllColumnsWithOperators(true, [
        'meterName',
        'term',
        'offerId',
        'subscriptionGuid',
        'tpId',
        'serviceId',
        'productName',
        'coreSize',
        'productTier',
        'skuName',
        'imageType',
        'customerId',
      ]),
    [],
  );
  const calcFeatureToFormikValue = (calcFeature, storeInfo) => {
    const featureDimensions = getDimensionsForCalculationFeatureType(calcFeature.featureType);
    const dimensions = [].concat(featureDimensions);

    calcFeature.dimensions.forEach(dim => {
      const dimension = allColumns.find(col => col.key === dim);
      if (dimension && !dimensions.find(col => col.key === dimension.key)) {
        dimensions.push(dimension);
      }
    });

    const calculations = calcFeature.calculations
      .map(
        pivotWider(
          mapValues(keyBy(dimensions, 'key'), 'pivotWiderTransformer'),
          defaultPivotWiderTransformer,
          storeInfo,
          'effectiveDate',
          'terminationDate',
          'calcParams',
          'filterId',
        ),
      )
      .map(calc => ({
        ...calc,
        ...calc.calcParams,
      }));

    return {
      ...calcFeature,
      dimensions,
      calculations,
    };
  };
  Object.keys(calcFeatureTypes).forEach(key => {
    const calcFeature = calcFeatureTypes[key];
    if (calcFeature.silos.indexOf(contract.silo) >= 0) {
      calcFeatureOptions.push({ key, text: calcFeature.text });
    }
  });

  const initial = calculationFeature
    ? calcFeatureToFormikValue(calculationFeature, store)
    : defaultCalcFeature();
  const dateColumns = [
    effectiveDateColumn(contract?.effectiveDate, contract?.terminationDate, 'calculations'),
    expirationDateColumn(contract?.effectiveDate, contract?.terminationDate, 'calculations'),
  ];

  const submitHandler = async values => {
    const featureDimensions = getDimensionsForCalculationFeatureType(values.featureType);
    const filterDimensions = [].concat(featureDimensions);
    values.dimensions?.forEach(dim => {
      if (!filterDimensions.find(col => col.key === dim.key)) {
        filterDimensions.push(dim);
      }
    });
    const feature = {
      ...values,
      dimensions: filterDimensions.map(d => d.key),
      calculations: values.calculations
        .map(filter => {
          return {
            ...filter,
            type: 'include',
            effectiveDate: moment.utc(filter.effectiveDate).format('YYYY-MM-DD'),
            terminationDate: moment.utc(filter.terminationDate).format('YYYY-MM-DD'),
            calcParams: getCalcParams(values, filter),
          };
        })
        .map(
          rowTransformer(
            { ...filterDimensions },
            defaultPivotWiderTransformer,
            'filterId',
            'type',
            'effectiveDate',
            'terminationDate',
            'calcParams',
            'featureType',
            'calculationMethod',
          ),
        ),
    };

    return onSubmit(feature);
  };

  const rowValidator = calcFields => row => {
    const missingFields = [];
    let dateErrors;
    const minMaxErrors = [];
    // validate calculation fields
    calcFields.forEach(column => {
      if (!row[column.key]) {
        missingFields.push(column.name);
      } else if (
        column.props?.inputProps &&
        (row[column.key] < column.props.inputProps.min ||
          row[column.key] > column.props.inputProps.max)
      ) {
        minMaxErrors.push(
          `${column.detailName || column.name} on must be any number between ${
            column.props.inputProps.min
          } and ${column.props.inputProps.max}`,
        );
      }
    });

    // if any fields has invalid values return
    if (minMaxErrors.length) {
      return `${minMaxErrors.join('. ')}. Please edit the highlighted row(s). `;
    }

    // validate that all date fields are there
    dateColumns.forEach(column => {
      if (!row[column.key]) {
        missingFields.push(column.name);
      } else if (
        new Date(row[column.key]) > new Date(contract.terminationDate) ||
        new Date(row[column.key]) < new Date(contract.effectiveDate)
      ) {
        dateErrors = `Invalid ${column.name}. Please edit the highlighted row(s).`;
      }
    });

    if (dateErrors) return dateErrors;
    if (missingFields.length > 0)
      return `Missing Required Fields: ${missingFields.join(
        ', ',
      )}. Please edit the highlighted row(s).`;

    return undefined;
  };

  const formValidator = values => {
    const featureType = !values.featureType ? 'Feature type must be provided' : undefined;
    const calculationMethod = !values.calculationMethod
      ? 'Calculation method must be provided'
      : undefined;

    if (values.dimensions?.length === 0) {
      const calculationErrors = ['At least one dimension must be provided'];
      return { calculationErrors };
    }
    const dimensions =
      values.dimensions.length === 0 ? 'At least one dimension must be provided' : undefined;

    if (featureType || calculationMethod || dimensions)
      return { featureType, calculationMethod, dimensions };

    if (values.calculations.length === 0) {
      const calculationErrors = ['At least one calculation must be assigned'];
      return { calculationErrors };
    }

    const featureDimensions = getDimensionsForCalculationFeatureType(values.featureType);
    const calculationFields = getCalcColumns(values);
    const filterTransformer = rowTransformer(
      {
        ...calculationFields,
        ...values.dimensions.map(x => x.key),
        ...featureDimensions.map(x => x.key),
      },
      defaultTransformer,
      'effectiveDate',
      'terminationDate',
      'type',
      ...calculationFields,
    );
    const calculationValidation = values.calculations
      .map(f => ({ ...f, dimensions: filterTransformer(f).dimensions }))
      .map(rowValidator([...getCalcMethod(values).fields]));
    const calculationErrors = calculationValidation.filter(e => e !== undefined);
    if (calculationErrors.length === 0) return {};

    return { calculations: calculationValidation, calculationErrors: uniq(calculationErrors) };
  };

  return (
    <Formik
      validate={formValidator}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={submitHandler}
      initialValues={initial}
    >
      {({ values, submitForm, isSubmitting, errors }) => {
        const onRenderRow = props => {
          const customStyle = {
            root: {
              backgroundColor: theme.palette.neutralWhite,
              selectors: {
                '& .ms-DetailsRow-fields .ms-DetailsRow-cell': {
                  padding: `${pxToRem(8)} ${pxToRem(4)}`,
                },
                '& .ms-Dropdown-container > .ms-Stack': {
                  marginBottom: `${pxToRem(0)} !important`,
                },
              },
            },
          };
          // eslint-disable-next-line react/prop-types
          if (errors.calculations && errors.calculations[props.itemIndex])
            customStyle.root = {
              backgroundColor: theme.palette.accentErrorOpaque,
              ':active': { backgroundColor: theme.palette.accentErrorOpaque },
              ':hover': { backgroundColor: theme.palette.accentErrorOpaque },
            };
          return <DetailsRow {...props} styles={customStyle} data-testid={CalculationRowTestId} />;
        };

        const calcMethods = values.featureType
          ? calcFeatureTypes[values.featureType]?.calculationMethods
          : {};
        const note = values.featureType ? calcFeatureTypes[values.featureType]?.note : null;
        const calcMethodOptions = Object.keys(calcMethods).map(key => ({
          key,
          text: calcMethods[key].text,
        }));

        const getCalculationColumns = () => {
          // added in case the feature type changes and the options are now different
          return calcMethods[values.calculationMethod]
            ? calcMethods[values.calculationMethod]?.fields.map(field => ({
                component: TextField,
                maxWidth: 75,
                ...field,
                text: field.name,
              }))
            : [];
        };

        const calculationColumns = getCalculationColumns();
        const featureDimensionColumns = getDimensionsForCalculationFeatureType(values.featureType);

        return (
          <>
            {errors.calculationErrors && errors.calculationErrors.length > 0 && (
              <MessageBar
                className={styles.errorBar}
                isMultiline
                messageBarType={MessageBarType.error}
                messageBarIconProps={{ iconName: 'Info' }}
              >
                Cannot save filters:
                {errors.calculationErrors.map(error => {
                  return <li key={error.message}>{error}</li>;
                })}
              </MessageBar>
            )}
            <Form className={styles.formStyle}>
              <div className={styles.formFilters}>
                <StackGrid smCols={1} mdCols={3} lgCols={3}>
                  <Field
                    name="featureType"
                    label="Calculation Feature Type"
                    component={Dropdown}
                    options={sortBy(calcFeatureOptions, 'text')}
                    data-testid={CalcFeatureTypeTestId}
                  />
                  <Field
                    name="calculationMethod"
                    label="Calculation Method"
                    component={Dropdown}
                    options={calcMethodOptions}
                    data-testid={CalcMethodTestId}
                  />
                </StackGrid>
              </div>
              <div>
                {note && (
                  <MessageBar
                    isMultiline
                    messageBarType={MessageBarType.info}
                    dismissButtonAriaLabel="Close"
                    className={styles.note}
                    nowrap
                  >
                    <div className="title">{note.title}</div>
                    {note.lines?.map(line => {
                      return <p>{line}</p>;
                    })}
                  </MessageBar>
                )}
              </div>
              <div className={styles.formFilters}>
                <Field
                  className={styles.dimensionPicker}
                  name="dimensions"
                  label="Transaction Dimensions"
                  component={MultiPickerFormik}
                  multiSelect
                  options={allColumns}
                  data-testid={DimensionsPickerTestId}
                />
                <FieldArray name="calculations">
                  {arrayHelpers => {
                    const columns = uniq([
                      ...calculationColumns,
                      ...featureDimensionColumns,
                      ...values.dimensions,
                      ...dateColumns,
                      menuColumn(),
                    ]);
                    return (
                      <>
                        {values.calculations.length > 0 && (
                          <Field
                            name="calculations"
                            component={DynamicDetailsList}
                            arrayHelpers={arrayHelpers}
                            selectionMode={SelectionMode.none}
                            columns={columns}
                            onRenderRow={onRenderRow}
                          />
                        )}
                        <div className={styles.buttonContainer}>
                          <TextButton
                            type="button"
                            data-testid={AddAssignmentButtonTestId}
                            disabled={isSubmitting}
                            onClick={() => {
                              arrayHelpers.push(createNewAssignment(contract));
                            }}
                          >
                            Add Assignment
                          </TextButton>
                        </div>
                      </>
                    );
                  }}
                </FieldArray>
              </div>

              <div className={styles.formActions}>
                <DefaultButton
                  className={styles.cancelButton}
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </DefaultButton>
                <PrimaryButton
                  onClick={submitForm}
                  disabled={isSubmitting}
                  data-testid={SaveAndContinueButtonTestId}
                >
                  Save and Continue
                  {isSubmitting && (
                    <Spinner
                      data-testid="create-agreement-spinner"
                      size={SpinnerSize.medium}
                      className={styles.loadingSpinner}
                    />
                  )}
                </PrimaryButton>
              </div>
            </Form>
          </>
        );
      }}
    </Formik>
  );
};

CalculationFeatureForm.propTypes = {
  calculationFeature: PropTypes.object,
  contract: PropTypes.object.isRequired,
  store: PropTypes.object.isRequired,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
};

CalculationFeatureForm.defaultProps = {
  calculationFeature: undefined,
  onSubmit: undefined,
  onCancel: undefined,
};

export default CalculationFeatureForm;
